import React, { memo, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import {
    AI_RISK_LEVEL_COLOR,
    sortAiRiskDistributionItems,
} from "../ai-risk-chart-constants";

const AI_RISK_DISTRIBUTION_FOOTNOTE_KEY = "aiRiskDistributionFootnote";

const AiRiskDistribution = memo(({ t, distribution = [], totalAiAssets = 0, isFetching }) => {
    const sorted = useMemo(() => sortAiRiskDistributionItems(distribution), [distribution]);

    const categories = useMemo(
        () =>
            sorted.map((row) => ({
                title: row.title,
                amount: Number(row.amount) || 0,
                percent: Number(row.percent) || 0,
                level: row.level,
            })),
        [sorted]
    );

    const chartCategories = useMemo(
        () => categories.filter((row) => row.amount > 0),
        [categories]
    );

    const { series, labels, colors } = useMemo(() => {
        const chartRows = chartCategories.map((row) => ({
            title: row.title,
            amount: row.amount,
            percent: row.percent,
            level: row.level,
        }));

        return {
            series: chartRows.map((row) => row.amount),
            labels: chartRows.map((row) => t(row.title)),
            colors: chartRows.map((row) => AI_RISK_LEVEL_COLOR[row.level] || "#90A4AE"),
        };
    }, [chartCategories, t]);

    const total = useMemo(() => {
        const fromCategories = categories.reduce((sum, row) => sum + row.amount, 0);
        return Number(totalAiAssets) || fromCategories;
    }, [categories, totalAiAssets]);

    const footnote = useMemo(() => {
        if (!sorted.length) {
            return null;
        }

        const withPercent = sorted.filter((row) => Number(row.percent) > 0);
        if (!withPercent.length) {
            return null;
        }

        const highlight = [...withPercent].sort(
            (a, b) => Number(b.percent) - Number(a.percent)
        )[0];

        const riskLabel = t(highlight.title);
        return t(AI_RISK_DISTRIBUTION_FOOTNOTE_KEY, {
            percent: highlight.percent,
            risk: riskLabel,
        });
    }, [sorted, t]);

    const hasData = series.length > 0 && series.some((value) => Number(value) > 0);

    const chartOptions = useMemo(
        () => ({
            chart: { type: "donut", toolbar: { show: false } },
            labels,
            colors,
            legend: { show: false },
            dataLabels: {
                enabled: true,
                formatter: (_val, opts) => {
                    const row = chartCategories[opts.seriesIndex];
                    if (!row || Number(row.percent) <= 0) {
                        return "";
                    }
                    return `${Math.round(Number(row.percent))}%`;
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: "72%",
                        labels: {
                            show: true,
                            name: { show: false },
                            value: { show: false },
                            total: {
                                show: true,
                                showAlways: true,
                                label: t("Total"),
                                fontSize: "13px",
                                fontWeight: 500,
                                formatter: () => String(total),
                            },
                        },
                    },
                },
            },
            stroke: { show: false },
        }),
        [chartCategories, colors, labels, t, total]
    );

    const emptyOptions = useMemo(
        () => ({
            ...chartOptions,
            labels: [t("There is no data")],
            colors: ["#e0e0e0"],
            plotOptions: {
                pie: {
                    donut: {
                        size: "72%",
                        labels: { show: false },
                    },
                },
            },
            dataLabels: { enabled: false },
        }),
        [chartOptions, t]
    );

    if (isFetching) {
        return (
            <Card className="mb-3 card_box_Shadow flex-grow-1" style={{ height: "460px" }}>
                <CardBody style={{ height: "100%" }}>
                    <div className="dt-field dt-skeleton mb-3" style={{ width: "60%", height: "24px" }} />
                    <div className="dt-field dt-skeleton" style={{ width: "100%", height: "360px" }} />
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className="mb-3 card_box_Shadow flex-grow-1" style={{ height: "460px" }}>
            <CardBody
                style={{ height: "100%", display: "flex", flexDirection: "column" }}
                className="ai-risk-distribution-card"
            >
                <CardTitle tag="h6" className="mb-3">
                    {t("AI Risk Distribution")}
                </CardTitle>

                <Row className="align-items-center flex-grow-1 g-3">
                    <Col xs={12} sm={5} lg={4}>
                        <div className="d-flex flex-column gap-3">
                            {categories.map((row) => (
                                <div
                                    key={row.level}
                                    className="d-flex align-items-center gap-2"
                                >
                                    <i
                                        className="mdi mdi-circle font-size-10 flex-shrink-0"
                                        style={{
                                            color:
                                                AI_RISK_LEVEL_COLOR[row.level] || "#90A4AE",
                                        }}
                                    />
                                    <span className="mb-0 small text-truncate">
                                        {t(row.title)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Col>

                    <Col xs={12} sm={7} lg={8}>
                        <div className="apex-charts">
                            <ReactApexChart
                                key={`${series.join(",")}-${labels.join(",")}`}
                                options={hasData ? chartOptions : emptyOptions}
                                series={hasData ? series : [1]}
                                type="donut"
                                height={280}
                            />
                        </div>
                    </Col>
                </Row>

                {footnote ? (
                    <p className="text-muted small mt-3 mb-0">{footnote}</p>
                ) : null}
            </CardBody>
        </Card>
    );
});

export default withNamespaces()(AiRiskDistribution);
